/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/QaInspectionNavigator.tsx
 * FILE_ID: QA-UI-INSPECTION-NAV-001
 * PURPOSE: QAs box. The grouped card is absent until a group exists.
 */
import { useEffect, useState, type MouseEvent } from 'react'
import { ChevronDown, ChevronRight, Loader2, Plus, Settings } from 'lucide-react'
import { confirmDialog } from '@quotech/ui'
import { QaNavigatorRow } from './QaNavigatorRow'
import { promptQaDuplicateCount } from './promptQaDuplicateCount'
import { cn } from '@quotech/tenant-shared/lib/utils'
import { InlineRenameInput } from '../../qa/InlineRenameInput'
import type { QaInspectionSummary } from '../../qa/qaApi'
import { useQaBulkSelection } from './useQaBulkSelection'
import { inspectionGroupChoices } from './qaInspectionMenu'

const ROW_ICON = 'rounded-md p-1 text-muted-foreground hover:text-primary hover:bg-background'

function subsetSelection(ids: readonly string[], isSelected: (id: string) => boolean) {
  const count = ids.filter(isSelected).length
  return {
    checked: ids.length > 0 && count === ids.length,
    indeterminate: count > 0 && count < ids.length,
  }
}

export type QaMenuOpenEvent = MouseEvent | { clientX: number; clientY: number }

export function QaInspectionNavigator({
  inspections,
  activeInspectionId,
  jobId,
  layout,
  onSelect,
  onDuplicate,
  onDuplicateToJob,
  onRenameSubmit,
  renamingId = null,
  renameRowId = null,
  onRenameRowStarted,
  onDelete,
  onAssignGroup,
  onDeleteSelected,
  onDuplicateSelected,
  onOpenMenu,
  jobRequest,
  onJobRequestClear,
  groupRequestIds,
  onGroupRequestClear,
  onOpenSettings,
  settingsDisabled = false,
  onAddBlank,
  blankPending = false,
}: {
  inspections: readonly QaInspectionSummary[]
  activeInspectionId: string | null
  jobId: number
  layout: 'row' | 'sheet'
  onSelect: (id: string) => void
  onDuplicate: (inspection: QaInspectionSummary, count: number) => void
  onDuplicateToJob: (inspection: QaInspectionSummary, jobNumber: string) => Promise<QaInspectionSummary>
  onRenameSubmit: (inspection: QaInspectionSummary, name: string) => void
  renamingId?: string | null
  renameRowId?: string | null
  onRenameRowStarted?: () => void
  onDelete: (inspection: QaInspectionSummary) => void
  onAssignGroup: (body: {
    inspection_ids: string[]
    job_id: string
    group_id?: string | null
    group_name?: string
  }) => void
  onDeleteSelected: (ids: string[]) => void | Promise<void>
  onDuplicateSelected: (ids: string[], count: number) => void | Promise<void>
  onOpenMenu: (inspection: QaInspectionSummary, event: QaMenuOpenEvent, moveIds: string[]) => void
  jobRequest: QaInspectionSummary | null
  onJobRequestClear: () => void
  groupRequestIds: string[] | null
  onGroupRequestClear: () => void
  onOpenSettings?: () => void
  settingsDisabled?: boolean
  onAddBlank?: () => void
  blankPending?: boolean
}) {
  // Reused from: packages/job-hub/src/jobs/tabs/job-qa/useQaBulkSelection.ts (useQaBulkSelection)
  const selection = useQaBulkSelection(inspections.map((row) => row.id))
  const [jobDraft, setJobDraft] = useState('')
  const [copiedJobId, setCopiedJobId] = useState<string | null>(null)
  const activeGroupId = inspections.find((row) => row.id === activeInspectionId)?.group_id ?? null
  const [qasOpen, setQasOpen] = useState(true)
  const [groupsOpen, setGroupsOpen] = useState(true)
  const [openGroupIds, setOpenGroupIds] = useState<ReadonlySet<string>>(() => new Set(activeGroupId ? [activeGroupId] : []))
  useEffect(() => {
    if (!activeGroupId) return
    setOpenGroupIds((current) => {
      if (current.has(activeGroupId)) return current
      const next = new Set(current)
      next.add(activeGroupId)
      return next
    })
  }, [activeGroupId])
  const [moveOpen, setMoveOpen] = useState(false)
  const [pendingGroupIds, setPendingGroupIds] = useState<string[] | null>(null)
  const sheet = layout === 'sheet'
  const iconButton = sheet ? 'inline-flex h-11 w-11 items-center justify-center' : cn(ROW_ICON, 'opacity-0 group-hover/qa-row:opacity-100')
  const headerButton = cn(
    'flex items-center gap-1 text-sm font-semibold text-foreground',
    sheet ? 'min-h-[44px]' : 'md:min-h-0',
  )
  const choiceButton = cn(
    'rounded-md border border-border px-3 text-sm',
    sheet ? 'min-h-[44px]' : 'py-1',
  )
  const namingIds = groupRequestIds ?? pendingGroupIds
  const groupNameOpen = namingIds != null
  const jobOpen = jobRequest != null && !groupNameOpen
  const groups = inspectionGroupChoices(inspections)
  const headerIcon = cn(
    'inline-flex shrink-0 items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-40',
    sheet ? 'min-h-[44px] min-w-[44px]' : 'h-8 w-8',
  )
  const ungrouped = inspections.filter((row) => !row.group_id)
  const ungroupedIds = ungrouped.map((row) => row.id)
  const groupedIds = inspections.filter((row) => row.group_id).map((row) => row.id)
  const ungroupedSelection = subsetSelection(ungroupedIds, selection.isSelected)
  const groupedSelection = subsetSelection(groupedIds, selection.isSelected)

  useEffect(() => {
    setJobDraft('')
  }, [jobRequest?.id])

  async function copyToJob() {
    const text = jobDraft.trim()
    if (!text || !jobRequest) return
    const created = await onDuplicateToJob(jobRequest, text)
    onJobRequestClear()
    setJobDraft('')
    if (created.job_id != null && String(created.job_id) !== String(jobId)) {
      setCopiedJobId(String(created.job_id))
    }
  }

  function showQas() {
    setQasOpen((open) => !open)
  }

  function showGroups() {
    setGroupsOpen((open) => !open)
  }

  function toggleGroup(id: string) {
    setOpenGroupIds((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function setIds(ids: readonly string[], selected: boolean) {
    // Reused from: packages/job-hub/src/jobs/tabs/job-qa/useQaBulkSelection.ts (useQaBulkSelection)
    if (selected) selection.selectIds(ids)
    else selection.deselectIds(ids)
  }

  async function confirmDelete() {
    const ids = [...selection.selected]
    // Reused from: packages/ui/src/components/ConfirmDialog.tsx (confirmDialog)
    const ok = await confirmDialog({
      title: ids.length === 1 ? 'Delete 1 QA?' : `Delete ${ids.length} QAs?`,
      description: 'This deletes the selected QA lists.',
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      variant: 'danger',
    })
    if (!ok) return
    await onDeleteSelected(ids)
  }

  async function confirmDuplicate() {
    const ids = [...selection.selected]
    // Reused from: packages/job-hub/src/jobs/tabs/job-qa/promptQaDuplicateCount.ts (promptQaDuplicateCount)
    const count = await promptQaDuplicateCount(ids.length)
    if (count == null) return
    await onDuplicateSelected(ids, count)
  }

  async function duplicateOne(row: QaInspectionSummary) {
    const count = await promptQaDuplicateCount(1)
    if (count == null) return
    await onDuplicate(row, count)
  }

  function assignSelected(groupId?: string) {
    onAssignGroup({
      inspection_ids: selection.selected,
      job_id: String(jobId),
      ...(groupId ? { group_id: groupId } : {}),
    })
  }

  function renderRow(row: QaInspectionSummary) {
    return (
      <QaNavigatorRow
        key={row.id}
        inspection={row}
        active={row.id === activeInspectionId}
        selected={selection.isSelected(row.id)}
        iconButton={iconButton}
        onToggle={() => selection.toggle(row.id)}
        onSelect={() => onSelect(row.id)}
        onDuplicate={() => void duplicateOne(row)}
        renaming={renamingId === row.id}
        renameRowId={renameRowId}
        onRenameRowStarted={onRenameRowStarted}
        onRenameSubmit={onRenameSubmit}
        onDelete={() => onDelete(row)}
        onOpenMenu={(event) => onOpenMenu(row, event, [row.id])}
      />
    )
  }

  const boxClass = cn(
    'rounded-xl border border-border bg-card',
    layout === 'row' ? 'w-[280px] max-w-full shrink-0' : 'w-full',
  )
  return (
    <div className={cn(
      'flex flex-row flex-wrap items-start gap-2',
      layout === 'row' ? 'w-fit max-w-full shrink-0' : 'w-full',
    )}>
    <section
      aria-label="QAs"
      className={boxClass}
      onClick={() => {
        if (copiedJobId) setCopiedJobId(null)
      }}
    >
      <div className="flex flex-wrap items-center gap-2 px-3 py-2">
        {/* Reused from: packages/job-hub/src/jobs/tabs/job-qa/QaBulkAssignBar.tsx (select-all checkbox) */}
        <input
          type="checkbox"
          checked={ungroupedSelection.checked}
          ref={(node) => { if (node) node.indeterminate = ungroupedSelection.indeterminate }}
          onChange={(event) => setIds(ungroupedIds, event.target.checked)}
          aria-label="Select all ungrouped QAs"
          className="h-3.5 w-3.5 rounded border-border"
        />
        <button type="button" className={headerButton} aria-expanded={qasOpen} aria-label="QAs" onClick={showQas}>
          QAs
          {/* Reused from: packages/job-hub/src/jobs/tabs/job-qa/JobQaSetupStrip.tsx (ChevronDown toggle) */}
          <ChevronDown className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', qasOpen && 'rotate-180')} aria-hidden />
        </button>
        {selection.count > 0 ? (
          <>
            <button
              type="button"
              className={headerButton}
              onClick={() => {
                if (groups.length === 0) setPendingGroupIds(selection.selected)
                else setMoveOpen((open) => !open)
              }}
            >
              Move to group
            </button>
            <button type="button" className={headerButton} onClick={() => void confirmDuplicate()}>
              Duplicate
            </button>
            <button type="button" className={headerButton} onClick={() => void confirmDelete()}>
              Delete
            </button>
          </>
        ) : null}
        <div className="ml-auto flex shrink-0 items-center">
          <button
            type="button"
            className={headerIcon}
            aria-label="Add blank checklist"
            title="Add blank checklist"
            disabled={blankPending}
            onClick={onAddBlank}
          >
            {blankPending
              ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              : <Plus className="h-4 w-4" aria-hidden />}
          </button>
          <button
            type="button"
            className={headerIcon}
            aria-label="Settings (records)"
            title="Settings (records)"
            disabled={settingsDisabled}
            onClick={onOpenSettings}
          >
            <Settings className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </div>
      {jobOpen ? (
        <div className="flex flex-wrap items-end gap-2 px-3 pb-2">
          <label className="min-w-0 flex-1 text-xs text-muted-foreground">
            Job number
            <input
              value={jobDraft}
              onChange={(event) => setJobDraft(event.target.value)}
              className={cn(
                'mt-1 w-full rounded-lg border border-border bg-background px-3',
                sheet ? 'min-h-[44px] text-base' : 'h-8 text-sm',
              )}
            />
          </label>
          <button
            type="button"
            className={cn('rounded-md bg-primary px-3 text-sm text-primary-foreground', sheet ? 'min-h-[44px]' : 'h-8')}
            onClick={(event) => {
              event.stopPropagation()
              void copyToJob()
            }}
          >
            Copy
          </button>
          <button
            type="button"
            className={cn('rounded-md border border-border px-3 text-sm', sheet ? 'min-h-[44px]' : 'h-8')}
            onClick={(event) => {
              event.stopPropagation()
              onJobRequestClear()
              setJobDraft('')
            }}
          >
            Cancel
          </button>
        </div>
      ) : null}
      {copiedJobId ? <p className="px-3 pb-2 text-xs text-muted-foreground">Copied to job {copiedJobId}</p> : null}
      {groupNameOpen ? (
        <div className="px-3 pb-2">
          {/* Reused from: packages/job-hub/src/jobs/qa/InlineRenameInput.tsx (InlineRenameInput) */}
          <InlineRenameInput
            initialValue=""
            ariaLabel="Group name"
            onSubmit={(name) => {
              onAssignGroup({
                inspection_ids: namingIds ?? [],
                job_id: String(jobId),
                group_name: name,
              })
              if (groupRequestIds != null) onGroupRequestClear()
              setPendingGroupIds(null)
            }}
            onCancel={() => {
              if (groupRequestIds != null) onGroupRequestClear()
              setPendingGroupIds(null)
            }}
          />
        </div>
      ) : null}
      {selection.count > 0 && moveOpen ? (
        <div className="flex flex-wrap gap-1 px-3 pb-2" role="group" aria-label="Move to group">
          <button type="button" className={choiceButton} onClick={() => assignSelected()}>Ungrouped</button>
          {groups.map((group) => (
            <button key={group.id} type="button" className={choiceButton} onClick={() => assignSelected(group.id)}>
              {group.name}
            </button>
          ))}
          <button type="button" className={choiceButton} onClick={() => setPendingGroupIds(selection.selected)}>
            New group
          </button>
        </div>
      ) : null}
      {qasOpen ? <div className="max-h-[45vh] overflow-auto">{ungrouped.map(renderRow)}</div> : null}
    </section>
    {groups.length > 0 ? (
    <section
      aria-label="Grouped QAs"
      className={boxClass}
    >
      <div className="flex flex-wrap items-center gap-2 px-3 py-2">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          <input
            type="checkbox"
            checked={groupedSelection.checked}
            ref={(node) => { if (node) node.indeterminate = groupedSelection.indeterminate }}
            onChange={(event) => setIds(groupedIds, event.target.checked)}
            aria-label="Select all grouped QAs"
            className="h-3.5 w-3.5 rounded border-border"
          />
          <button type="button" className={headerButton} aria-expanded={groupsOpen} aria-label="Grouped QAs" onClick={showGroups}>
            Grouped QAs
            <ChevronDown className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', groupsOpen && 'rotate-180')} aria-hidden />
          </button>
        </div>
        <div className="pointer-events-auto ml-auto flex shrink-0 items-center">
          <button
            type="button"
            className={headerIcon}
            aria-label="Settings (records)"
            title="Settings (records)"
            disabled={settingsDisabled}
            onClick={onOpenSettings}
          >
            <Settings className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </div>
      {groupsOpen ? (
        <div className="max-h-[45vh] overflow-auto">
          {groups.map((group) => {
            const open = openGroupIds.has(group.id)
            const groupIds = inspections.filter((row) => row.group_id === group.id).map((row) => row.id)
            const groupSelection = subsetSelection(groupIds, selection.isSelected)
            return (
              <div key={group.id}>
                <div className={cn('flex items-center gap-2 px-3', sheet ? 'min-h-[44px]' : 'min-h-11')}>
                  <input
                    type="checkbox"
                    checked={groupSelection.checked}
                    ref={(node) => { if (node) node.indeterminate = groupSelection.indeterminate }}
                    aria-label={`Select group ${group.name}`}
                    onClick={(event) => event.stopPropagation()}
                    onChange={(event) => setIds(groupIds, event.target.checked)}
                    className="h-4 w-4 shrink-0"
                  />
                  <span className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">{group.name}</span>
                  <button
                    type="button"
                    className={cn(
                      'ml-auto inline-flex shrink-0 items-center justify-center',
                      sheet ? 'min-h-[44px] min-w-[44px]' : 'h-8 w-8',
                    )}
                    aria-expanded={open}
                    aria-label={`Expand ${group.name}`}
                    onClick={() => toggleGroup(group.id)}
                  >
                    <ChevronRight className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-90')} aria-hidden />
                  </button>
                </div>
                {open ? (
                  <div className="pl-4">
                    {inspections.filter((row) => row.group_id === group.id).map(renderRow)}
                  </div>
                ) : null}
              </div>
            )
          })}
        </div>
      ) : null}
    </section>
    ) : null}
    </div>
  )
}

